import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plug, CheckCircle2, X, RefreshCw, Activity, KeyRound, Lightbulb, Loader2 } from "lucide-react";

const HEALTH_STYLE = {
  connected: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  healthy: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  degraded: "bg-amber-50 text-amber-700 ring-amber-200",
  authentication_failed: "bg-rose-50 text-rose-700 ring-rose-200",
  not_configured: "bg-slate-100 text-slate-500 ring-slate-200",
  disabled: "bg-slate-100 text-slate-400 ring-slate-200",
};
const HEALTH_LABEL = {
  connected: "Connected", healthy: "Healthy", degraded: "Degraded",
  authentication_failed: "Authentication Failed", not_configured: "Not set up", disabled: "Disabled",
};
const REQ_LABEL = { required: "Required", optional: "Optional", not_required: "Not Required", disabled: "Disabled" };
const MODE_LABEL = {
  tedops_managed: "TedOps Managed", existing_company_connection: "Existing Company Connection",
  client_owned: "Client Owned", configure_later: "Configure Later",
};
const fmt = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
const sel = "appearance-none rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30";

function ReplaceCredsModal({ cid, prov, onClose, onDone }) {
  const [fields, setFields] = useState([{ k: "", v: "" }]);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const secrets = {};
    fields.forEach((f) => { if (f.k.trim() && f.v.trim()) secrets[f.k.trim()] = f.v.trim(); });
    if (!Object.keys(secrets).length) { toast.error("Add at least one credential field"); return; }
    setBusy(true);
    try { await api.put(`/platform/companies/${cid}/integrations/${prov.key}/credentials`, { secrets }); toast.success("Credentials replaced"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 backdrop-blur-sm p-4 sm:p-8" data-testid="replace-creds-modal">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl ring-1 ring-slate-200 my-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-slate-500" /><h2 className="text-base font-bold">Replace {prov.label} credentials</h2></div>
          <button onClick={onClose} data-testid="replace-creds-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-xs text-slate-400">Stored secrets are never shown. Entering new values replaces the existing ones.</p>
          {fields.map((f, i) => (
            <div key={i} className="flex gap-2">
              <input value={f.k} placeholder="field (e.g. api_key)" onChange={(e) => setFields((p) => p.map((x, j) => j === i ? { ...x, k: e.target.value } : x))} data-testid={`cred-key-${i}`} className="w-2/5 rounded-lg border border-slate-200 px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
              <input value={f.v} type="password" placeholder="value" onChange={(e) => setFields((p) => p.map((x, j) => j === i ? { ...x, v: e.target.value } : x))} data-testid={`cred-val-${i}`} className="flex-1 rounded-lg border border-slate-200 px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
            </div>
          ))}
          <button onClick={() => setFields((p) => [...p, { k: "", v: "" }])} className="text-xs font-semibold text-blue-600 hover:text-blue-700">+ Add field</button>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="replace-creds-save" className="rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold text-sm px-4 py-2">{busy ? "Saving…" : "Replace credentials"}</button>
        </div>
      </div>
    </div>
  );
}

function IntegrationCard({ cid, it, meta, onChange }) {
  const [busy, setBusy] = useState(false);
  const [showCreds, setShowCreds] = useState(false);
  const cap = it.capabilities;
  const patch = async (body) => { setBusy(true); try { await api.put(`/platform/companies/${cid}/integrations/${it.key}`, body); onChange(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setBusy(false); } };
  const run = async (action) => { setBusy(true); try { const r = await api.post(`/platform/companies/${cid}/integrations/${it.key}/${action}`); if (action === "test-connection") toast[r.data.ok ? "success" : "error"](r.data.detail || r.data.status); else toast.success("Rechecked"); onChange(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setBusy(false); } };

  return (
    <div className="rounded-xl ring-1 ring-slate-200 bg-white p-5" data-testid={`integration-${it.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Plug className="w-4 h-4 text-slate-500" /></span>
          <div>
            <div className="font-semibold text-sm text-slate-900 flex items-center gap-2">
              {it.label}
              {!it.supported && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 text-[10px] font-semibold uppercase">Unavailable</span>}
            </div>
            <div className="text-xs text-slate-400">{it.modules.length ? `Modules: ${it.modules.join(", ")}` : "Platform service"}</div>
          </div>
        </div>
        <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${HEALTH_STYLE[it.health.status] || HEALTH_STYLE.not_configured}`} data-testid={`integration-status-${it.key}`}>
          {HEALTH_LABEL[it.health.status] || it.health.status}
        </span>
      </div>

      {it.recommendation && (
        <div className="mt-3 flex items-start gap-1.5 text-xs text-indigo-600 bg-indigo-50/60 rounded-lg px-3 py-2">
          <Lightbulb className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span><span className="font-semibold">Recommended: {MODE_LABEL[it.recommendation]}</span> — {it.recommendation_reason}</span>
        </div>
      )}

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <label className="text-xs text-slate-500">Requirement
          <select value={it.requirement} disabled={busy || !it.supported} onChange={(e) => patch({ requirement: e.target.value })} data-testid={`integration-requirement-${it.key}`} className={`mt-1 w-full ${sel}`}>
            {meta.requirements.map((r) => <option key={r} value={r}>{REQ_LABEL[r]}</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-500">Configuration Mode
          <select value={it.setup_mode || ""} disabled={busy || !it.supported || it.requirement === "disabled"} onChange={(e) => patch({ setup_mode: e.target.value })} data-testid={`integration-mode-${it.key}`} className={`mt-1 w-full ${sel}`}>
            <option value="">—</option>
            {meta.setup_modes.filter((m) => {
              if (m === "tedops_managed") return cap.supports_tedops_managed;
              if (m === "client_owned" || m === "existing_company_connection") return cap.supports_client_owned;
              return true;
            }).map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
          </select>
        </label>
      </div>

      {/* Credential + health row */}
      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap border-t border-slate-100 pt-3">
        <div className="flex items-center gap-3 text-xs text-slate-500">
          {cap.credentials_required ? (
            it.credential.configured
              ? <span className="inline-flex items-center gap-1 text-emerald-600 font-medium" data-testid={`integration-cred-${it.key}`}><CheckCircle2 className="w-3.5 h-3.5" /> Configured</span>
              : <span className="text-slate-400" data-testid={`integration-cred-${it.key}`}>No credentials</span>
          ) : <span className="text-slate-400">No client credentials needed</span>}
          {it.health.last_success && <span>· Last success {fmt(it.health.last_success)}</span>}
          {it.health.last_checked && <span>· Checked {fmt(it.health.last_checked)}</span>}
          {it.health.last_error && <span className="text-rose-500" title={it.health.last_error}>· Last error</span>}
        </div>
        <div className="flex items-center gap-1.5">
          {cap.credentials_required && it.supported && (
            <button onClick={() => setShowCreds(true)} disabled={busy} data-testid={`integration-replace-${it.key}`} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold px-2.5 py-1.5"><KeyRound className="w-3.5 h-3.5" /> {it.credential.configured ? "Replace" : "Add"} credential</button>
          )}
          {cap.has_test_connection
            ? <button onClick={() => run("test-connection")} disabled={busy} data-testid={`integration-test-${it.key}`} className="inline-flex items-center gap-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-1.5"><Activity className="w-3.5 h-3.5" /> Test</button>
            : <span className="text-[10px] text-slate-300 px-1">Test unavailable</span>}
          {cap.has_recheck
            ? <button onClick={() => run("recheck")} disabled={busy} data-testid={`integration-recheck-${it.key}`} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold px-2.5 py-1.5"><RefreshCw className="w-3.5 h-3.5" /> Recheck</button>
            : null}
        </div>
      </div>
      {showCreds && <ReplaceCredsModal cid={cid} prov={it} onClose={() => setShowCreds(false)} onDone={() => { setShowCreds(false); onChange(); }} />}
    </div>
  );
}

export default function PlatformIntegrations() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = () => api.get(`/platform/companies/${id}/integrations`).then((r) => setData(r.data)).catch(() => toast.error("Could not load integrations"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) return <div className="min-h-screen bg-slate-50" data-testid="integrations-loading" />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-900" data-testid="platform-integrations-page">
      <div className="p-6 lg:p-10 max-w-5xl mx-auto">
        <button onClick={() => navigate(`/platform/onboarding/${id}`)} data-testid="integrations-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-5"><ArrowLeft className="w-4 h-4" /> Setup</button>
        <div className="flex items-center gap-2.5 mb-1">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm"><Plug className="w-[18px] h-[18px] text-white" /></span>
          <h1 className="text-2xl font-bold tracking-tight">Integration Setup</h1>
        </div>
        <p className="text-sm text-slate-500 mb-7 ml-0.5">{data.company.name} — only integrations relevant to the enabled modules are shown. Recommendations are advisory.</p>
        <div className="space-y-3">
          {data.integrations.length === 0 && <div className="text-sm text-slate-400 bg-white rounded-xl ring-1 ring-slate-200 p-8 text-center" data-testid="integrations-empty">No integrations are relevant to the currently enabled modules.</div>}
          {data.integrations.map((it) => <IntegrationCard key={it.key} cid={id} it={it} meta={data} onChange={load} />)}
        </div>
      </div>
    </div>
  );
}
